// Extracted from original/src/components/HelperComponents.tsx:333-560 (`WalletSelect`, ADR-08 §2.1) for the customer
// dialogs (C22, D03). Kept: trigger button, dropdown frame, search box, header title, option rows and their classes.
// Cut: the wallet-list filtering by id 11 / 12 and names (`isDollarVault`, `isCurrencyVault`), the sub-safe label and
// balance lookup (`companySettings`, `subSafeBalances`, `currentUser`) and `formatNumber` → the container passes the
// options already chosen for the currency (D-029: USD → the dollar vault; D-118: the operator's custody) with their
// labels and preformatted balances. Inline Arabic strings → t() (D-055).
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { useT } from '../i18n-context.js';

export interface WalletSelectOption {
  readonly id: string;
  readonly label: string;
  /** Preformatted balance, e.g. "1,000,000 د.ع" or "500 $". */
  readonly balanceLabel: string;
}

export interface WalletSelectProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly WalletSelectOption[];
  readonly required?: boolean;
  readonly headerTitle?: string;
  readonly placeholder?: string;
  readonly label?: string;
}

export function WalletSelect({ value, onChange, options, required = false, headerTitle, placeholder, label }: WalletSelectProps) {
  const t = useT();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchFilteredWallets = options.filter((w) => {
    if (!searchQuery.trim()) return true;
    return w.label.toLowerCase().includes(searchQuery.toLowerCase());
  });
  const selectedWallet = options.find((w) => w.id === value);

  return (
    <div ref={containerRef} className="relative w-full text-right" dir="rtl" data-testid="wallet-select">
      {label && (
        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 flex justify-between items-center focus:outline-none focus:border-blue-500 text-sm font-bold text-slate-800 dark:text-[#f0f6fc] cursor-pointer transition-all min-h-[48px] shadow-sm"
      >
        {selectedWallet ? (
          <div className="flex justify-between items-center w-full gap-2 pl-2">
            <span className="truncate font-bold text-gray-800 dark:text-white">{selectedWallet.label}</span>
            <span className="font-mono text-xs text-blue-600 dark:text-blue-400 font-bold shrink-0">
              ({selectedWallet.balanceLabel})
            </span>
          </div>
        ) : (
          <span className="text-gray-800 dark:text-gray-200 font-bold">
            {placeholder || t('cust.ws_select_box')}
          </span>
        )}
        <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute z-[9999] top-full left-0 right-0 mt-1.5 bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-72"
          >
            {/* Search Box at Top */}
            <div className="p-3 border-b border-gray-100 dark:border-[#30363d] bg-gray-50/80 dark:bg-[#161b22]/80 text-right">
              <input
                type="text"
                placeholder={t('cust.ws_search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent text-right font-bold text-gray-800 dark:text-white text-sm focus:outline-none placeholder-gray-400"
                autoFocus
              />
            </div>

            {/* Header Title */}
            <div className="px-4 py-2 bg-gray-50/50 dark:bg-[#161b22]/50 text-gray-400 dark:text-gray-500 font-bold border-b border-gray-100 dark:border-[#30363d] text-xs text-right">
              {headerTitle || t('cust.ws_header')}
            </div>

            <div className="overflow-y-auto custom-scrollbar divide-y divide-gray-50 dark:divide-[#30363d] flex-1 max-h-52">
              {!required && (
                <button
                  type="button"
                  onClick={() => {
                    onChange('');
                    setIsOpen(false);
                    setSearchQuery('');
                  }}
                  className={`w-full text-right px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#161b22] text-xs font-bold text-gray-400 transition-colors cursor-pointer ${!value ? 'bg-blue-50/30 dark:bg-blue-900/10 text-primary-600 dark:text-primary-400' : ''}`}
                >
                  {t('cust.ws_select_box')}
                </button>
              )}

              {searchFilteredWallets.length === 0 ? (
                <div className="p-4 text-center text-xs text-gray-400 font-bold">
                  {t('cust.ws_no_results')}
                </div>
              ) : (
                searchFilteredWallets.map((w) => {
                  const isSelected = w.id === value;
                  return (
                    <button
                      key={w.id}
                      type="button"
                      data-testid={`wallet-option-${w.id}`}
                      onClick={() => {
                        onChange(w.id);
                        setIsOpen(false);
                        setSearchQuery('');
                      }}
                      className={`w-full text-right px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#161b22] flex justify-between items-center transition-colors cursor-pointer ${
                        isSelected
                          ? 'bg-blue-50/80 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-black'
                          : 'text-slate-800 dark:text-slate-200'
                      }`}
                    >
                      <span className={`text-xs font-bold truncate ${isSelected ? 'font-black' : ''}`}>
                        {w.label}
                      </span>
                      <span className="font-mono text-xs text-slate-500 dark:text-slate-400 font-bold shrink-0 pl-2" dir="ltr">
                        {w.balanceLabel}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
